/**
 * App Shell
 * CCH Care — Phase 2+
 * Last Modified: 2026-06-26
 *
 * Changes (2026-06-26) — Grace pill timezone fix:
 * - The header "GRACE LEFT" pill (and the avatar popover's "Grace Window
 *   Left" row) previously recomputed remaining grace time client-side via
 *   computeGraceRemaining(): new Date(`${shift_date}T${end_time}`) + 2h −
 *   Date.now(). That parses a FACILITY wall-clock time (end_time, e.g.
 *   "07:00:00" at Mandeville) in the VIEWER's browser zone, so a US-Eastern
 *   reviewer of the Jamaica pilot saw the countdown short by the
 *   viewer↔facility offset (~1h in summer, more in winter) — directly
 *   contradicting the in-page grace banner, which reads the correct value.
 * - Replaced with the server-authoritative `graceMinutesRemaining` now
 *   surfaced by useShift (from the shift payload's grace_minutes_remaining,
 *   computed FACILITY-side by the backend — same source GraceWindowBanner
 *   and the ADL flow-sheet banner use). formatGrace() only formats an
 *   already-correct integer; it does no date/zone math.
 * - Deleted computeGraceRemaining() and LATE_DOC_GRACE_HOURS (the client
 *   no longer needs the grace-window constant; the backend owns it).
 * - Display format ("Xh Ym" / "Xm") and the 5-min tick cadence are
 *   unchanged — only the value is now right.
 *
 * Changes (2026-05-13) — Grace window UI parity:
 * - Pulled `inGraceWindow` from useShift().
 * - Header countdown pill now branches on inGraceWindow:
 *     · normal shift  → blue tint, mm:ss countdown, "REMAINING" label
 *     · grace window  → amber tint, "Xh Ym" countdown to grace end,
 *                       "GRACE LEFT" label
 *   Without this, the pill kept reading "0:00 REMAINING" + "🟢 On Shift"
 *   directly contradicting the grace-window banner sitting below it.
 * - shiftStatusLabel now reads "🟡 Grace Window" during grace instead
 *   of "🟢 On Shift" (same desync, just in the avatar popover).
 * - Avatar popover's "Time Remaining" row swaps to "Grace Window Left"
 *   during grace.
 *
 * Changes (2026-04-22):
 * - Removed onFabPress prop from BottomNav. The FAB was never wired to
 *   anything beyond a console.log stub, and BottomNav has been
 *   refactored to no longer render a FAB. See BottomNav.jsx changelog
 *   for the full rationale.
 *
 * Previous changes:
 * - User avatar now shows a popover on click with name, role,
 *   facility, shift status, and logout button.
 * - Reports tab wired to /dashboard/reports
 * - Header now shows Org Name · Facility Name when they differ,
 *   or just Facility Name for single-location orgs
 *
 * Phase 5f:
 * - decodeHtml() applied to first_name/last_name to handle
 *   HTML entities (e.g. O&#039;Meally) from PHP sanitize()
 * - Dark mode toggle (🌙 / ☀️) added to header via useTheme hook
 *
 * ESLint fixes:
 * - Removed unused switchFacility destructure
 * - Brand div given role/tabIndex/onKeyDown
 */

import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import useShift from '../../hooks/useShift';
import useNotifications from '../../hooks/useNotifications';
import useTheme from '../../hooks/useTheme';
import SessionTimeout from '../security/SessionTimeout';
import TrialBanner from '../ui/TrialBanner';
import BottomNav from '../../features/dashboard/components/BottomNav';
import ToastContainer from '../../features/dashboard/components/ToastContainer';
import NotificationPanel from '../../features/dashboard/components/NotificationPanel';
import { COLORS } from '../../config/constants';
import AccessDeniedHandler from '../security/AccessDeniedHandler';
import OutgoingShiftBanner from './OutgoingShiftBanner';
import CheckinBanner from '../layout/CheckinBanner';

/**
 * Decode HTML entities that PHP's htmlspecialchars() may have introduced.
 * Uses a textarea element so the browser does the decoding natively.
 * Safe — no innerHTML on any rendered element.
 */
function decodeHtml(str) {
  if (!str) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

/**
 * Format an already-computed minutes integer as "Xh Ym" or "Xm".
 *
 * IMPORTANT: this does NO date or timezone math. The minutes value is
 * computed server-side in the facility timezone (useShift's
 * graceMinutesRemaining, sourced from the shift payload's
 * grace_minutes_remaining). Do not reintroduce a client-side
 * end_time-based recompute here — that re-creates the viewer-zone skew
 * this fix removed.
 *
 * Returns null for null/undefined input so callers can fall back; '0m'
 * for a non-positive value.
 */
function formatGrace(mins) {
  if (mins === null || mins === undefined) return null;
  if (mins <= 0) return '0m';
  const hours = Math.floor(mins / 60);
  const m     = mins % 60;
  return hours > 0 ? `${hours}h ${m}m` : `${m}m`;
}

export default function AppShell() {
  const { user, facilities, memberships, logout } = useAuth();
  const {
    shift, shiftState, inGraceWindow, graceMinutesRemaining,
    timeDisplay, accessibleUnits,
    outgoingShift, isOutgoingMode, enterOutgoingMode, exitOutgoingMode,
  } = useShift();
  const notifications = useNotifications();
  const { isDark, toggleTheme } = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [activeTab, setActiveTab]   = useState('dashboard');
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    if (!avatarOpen) return;
    function handleClick(e) {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [avatarOpen]);

  // Sync active tab with URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/setup') || path.includes('/staff')) setActiveTab('more');
    else if (path.includes('/tasks'))     setActiveTab('tasks');
    else if (path.includes('/residents')) setActiveTab('residents');
    else if (path.includes('/messages'))  setActiveTab('messages');
    else if (path.includes('/reports'))   setActiveTab('reports');
    else if (path.includes('/more'))      setActiveTab('more');
    else setActiveTab('dashboard');
  }, [location.pathname]);

  const activeFacilityId = localStorage.getItem('care_facility_id');
  const activeFacility   = facilities.find(f => String(f.facility_id) === String(activeFacilityId));
  const memberRole       = memberships?.[0]?.role ?? user?.role ?? '';

  // Decode HTML entities from PHP sanitize() on name fields
  const firstName  = decodeHtml(user?.first_name);
  const lastName   = decodeHtml(user?.last_name);
  const memberName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  const initials   = (firstName?.[0] ?? '') + (lastName?.[0] ?? '');

  // Org name from membership tenant_name
  const orgName      = memberships?.[0]?.tenant_name ?? '';
  const facilityName = activeFacility?.name ?? activeFacility?.facility_name ?? '';

  // Header shows facility name only — org name is available in the avatar popover
  const headerLabel = orgName || facilityName || null;

  // Grace state overrides the "On Shift" label even though shiftState
  // is still 'active'. Order matters in this ternary.
  const shiftStatusLabel = inGraceWindow              ? '🟡 Grace Window'
                         : shiftState === 'active'    ? '🟢 On Shift'
                         : shiftState === 'upcoming'  ? '🟡 Shift Starting Soon'
                         : shiftState === 'ended'     ? '🔴 Shift Ended'
                         :                              '⚫ No Active Shift';

  // Server-authoritative grace countdown (facility-frame). Only formatted
  // during grace; the value is computed by the backend, not here.
  const graceRemaining = inGraceWindow ? formatGrace(graceMinutesRemaining) : null;

  const ROLE_COLORS_MAP = {
    'CNA':             '#2ECC71',
    'Care Supervisor': '#E67E22',
    'Care Admin':      '#1B6B93',
    'Nurse':           '#9B59B6',
    'Office Manager':  '#16A085',
    'Physician':       '#2980B9',
  };
  const roleColor = ROLE_COLORS_MAP[memberRole] || '#1B6B93';

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    switch (tab) {
      case 'dashboard': navigate('/dashboard'); break;
      case 'tasks':     navigate('/dashboard/tasks'); break;
      case 'residents': navigate('/dashboard/residents'); break;
      case 'messages':  navigate('/dashboard/messages'); break;
      case 'reports':   navigate('/dashboard/reports'); break;
      case 'more':      navigate('/dashboard/more'); break;
      default:          navigate('/dashboard');
    }
  };

  const handleLogout = () => {
    setAvatarOpen(false);
    logout();
  };

  const handleBrandClick = () => {
    setActiveTab('dashboard');
    navigate('/dashboard');
  };

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg }}>
      <SessionTimeout />
      <AccessDeniedHandler onToast={notifications.addToast} />

      {/* ── Header ── */}
      <header style={{
        background: COLORS.primary, color: '#fff',
        padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        position: 'sticky', top: 0, zIndex: 50, minHeight: 56,
      }}>
        {/* Brand */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleBrandClick}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBrandClick(); }}}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>CCH Care</div>
        </div>

        {/* Org · Facility / Unit label */}
        {accessibleUnits.length > 1 ? (
          <select value={shift?.unit_id || ''} readOnly style={{
            padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', outline: 'none', maxWidth: 160,
          }}>
            {accessibleUnits.map(u => (
              <option key={u.unit_id} value={u.unit_id} style={{ color: '#333' }}>{u.name}</option>
            ))}
          </select>
        ) : shift?.unit_name ? (
          // On shift — show facility · unit
          <span style={{
            fontSize: 12, opacity: 0.85, padding: '4px 8px',
            background: 'rgba(255,255,255,0.1)', borderRadius: 6,
            maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {headerLabel ? `${headerLabel} · ${shift.unit_name}` : shift.unit_name}
          </span>
        ) : headerLabel ? (
          <span style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            fontSize: 12, opacity: 0.85, fontWeight: 600,
            maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}>
            {headerLabel}
          </span>
        ) : null}

        <div style={{ flex: 1 }} />

        {/* Shift countdown pill — branches on grace state */}
        {shiftState === 'active' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {shift?.shift_type && (
              <div style={{
                padding: '6px 10px',
                background: 'rgba(255,255,255,0.18)',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                lineHeight: 1,
              }}>
                {{ AM: 'Day', PM: 'Evening', NOC: 'Night' }[shift.shift_type] ?? shift.shift_type}
              </div>
            )}
            {inGraceWindow ? (
              <div style={{
                textAlign: 'center',
                padding: '2px 10px',
                background: 'rgba(245, 158, 11, 0.28)',
                border: '1px solid rgba(245, 158, 11, 0.65)',
                borderRadius: 6,
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, fontFamily: 'monospace' }}>
                  {graceRemaining || '—'}
                </div>
                <div style={{ fontSize: 9, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  grace left
                </div>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '2px 10px',
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 6,
              }}>
                <div style={{ fontSize: 16, fontWeight: 800, fontFamily: 'monospace' }}>{timeDisplay}</div>
                <div style={{ fontSize: 9, opacity: 0.7, textTransform: 'uppercase' }}>remaining</div>
              </div>
            )}
          </div>
        )}

        {/* ── Avatar with popover ── */}
        <div ref={avatarRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setAvatarOpen(o => !o)}
            title={`${memberName} · ${memberRole}`}
            style={{
              width: 34, height: 34, borderRadius: '50%',
              background: avatarOpen ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)',
              border: avatarOpen ? '2px solid rgba(255,255,255,0.8)' : '2px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#fff',
              cursor: 'pointer', transition: 'all 0.15s',
              minHeight: 'unset',
            }}
          >
            {initials}
          </button>

          {/* Popover */}
          {avatarOpen && (
            <div style={{
              position: 'absolute', top: 42, right: 0,
              background: COLORS.surface, borderRadius: 12, padding: 0,
              width: 240, zIndex: 200,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: '1px solid ' + COLORS.border,
              overflow: 'hidden',
              animation: 'fadeSlideIn 0.15s ease-out',
            }}>
              {/* Profile header */}
              <div style={{ background: COLORS.primary, padding: '16px 16px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0,
                }}>
                  {initials}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{memberName}</div>
                  <span style={{
                    display: 'inline-block', marginTop: 3,
                    fontSize: 10, fontWeight: 700, color: '#fff',
                    background: roleColor, padding: '2px 8px',
                    borderRadius: 20, opacity: 0.95,
                  }}>
                    {memberRole}
                  </span>
                </div>
              </div>

              {/* Details */}
              <div style={{ padding: '10px 0' }}>
                {orgName && (
                  <PopoverRow icon="🏢" label="Organization" value={orgName} />
                )}
                {activeFacility && (
                  <PopoverRow icon="🏥" label="Facility" value={facilityName} />
                )}
                {shift?.unit_name && (
                  <PopoverRow icon="🚪" label="Unit" value={shift.unit_name} />
                )}
                <PopoverRow icon="⏰" label="Shift" value={shiftStatusLabel} />

                {/* Time remaining row — varies by grace state */}
                {shiftState === 'active' && !inGraceWindow && timeDisplay && (
                  <PopoverRow icon="⌛" label="Time Remaining" value={timeDisplay} />
                )}
                {shiftState === 'active' && inGraceWindow && (
                  <PopoverRow
                    icon="⏱"
                    label="Grace Window Left"
                    value={graceRemaining || 'expiring soon'}
                  />
                )}
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid ' + COLORS.border }} />

              {/* Actions */}
              <div style={{ padding: '8px 0' }}>
                <button
                  onClick={() => { setAvatarOpen(false); navigate('/dashboard/more'); }}
                  style={{
                    width: '100%', padding: '10px 16px', background: 'none', border: 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: 13, color: COLORS.text, cursor: 'pointer', textAlign: 'left',
                    minHeight: 'unset',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = COLORS.bg}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <span style={{ fontSize: 16 }}>⚙️</span> Settings &amp; More
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%', padding: '10px 16px', background: 'none', border: 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: 13, color: COLORS.danger, cursor: 'pointer', textAlign: 'left',
                    fontWeight: 600, minHeight: 'unset',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = COLORS.dangerLight}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <span style={{ fontSize: 16 }}>🚪</span> Sign Out
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Theme toggle ── */}
        <button
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8,
            color: '#fff', fontSize: 15, cursor: 'pointer',
            padding: '4px 8px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 'unset', width: 32, height: 32,
          }}
        >
          {isDark ? '☀️' : '🌙'}
        </button>

        {/* Notification bell */}
        <button onClick={() => notifications.setPanelOpen(true)} style={{
          position: 'relative', background: 'none', border: 'none',
          color: '#fff', fontSize: 20, cursor: 'pointer', padding: '4px', flexShrink: 0,
          minHeight: 'unset',
        }}>
          🔔
          {notifications.unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: -2, right: -4,
              background: COLORS.danger, color: '#fff',
              fontSize: 10, fontWeight: 700, minWidth: 16, height: 16,
              borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
            }}>
              {notifications.unreadCount > 9 ? '9+' : notifications.unreadCount}
            </span>
          )}
        </button>
      </header>

      <OutgoingShiftBanner
        outgoingShift={outgoingShift}
        isOutgoingMode={isOutgoingMode}
        onEnter={enterOutgoingMode}
        onExit={exitOutgoingMode}
      />
      <CheckinBanner />
      <TrialBanner />
      <ToastContainer toasts={notifications.toasts} onDismiss={notifications.dismissToast} />
      <NotificationPanel
        open={notifications.panelOpen}
        onClose={() => notifications.setPanelOpen(false)}
        notifications={notifications.notifications}
        unreadCount={notifications.unreadCount}
        onMarkRead={notifications.markRead}
        onMarkAllRead={notifications.markAllRead}
      />

      <main style={{ maxWidth: 960, margin: '0 auto', paddingBottom: 70 }}>
        <Outlet />
      </main>

      <BottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function PopoverRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px' }}>
      <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      </div>
    </div>
  );
}